import { DatePicker } from "@elit-hair/elit-ui";
import { useQueries } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { Fragment, useMemo, useState } from "react";
import { TbArrowLeft } from "react-icons/tb";

import { Link as AppLink } from "@/components/link";
import { getTeamMembersKpiByDateRange } from "@/hooks/queries/call.queries";
import { useTeams } from "@/hooks/queries/team/use-team-queries";
import type { KpiData } from "@/types/call.types";
import { getDateString } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

export const Route = createFileRoute(APP_ROUTES_FULL.TESTING_KPIS_REPORT)({
    component: KpisReportPage,
});

const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function getWeekRange(anchorDate: string): { weekStart: DateTime; weekEnd: DateTime; days: string[] } {
    const date = DateTime.fromISO(anchorDate);
    const daysToMonday = (date.weekday + 6) % 7;
    const weekStart = date.minus({ days: daysToMonday });
    const weekEnd = weekStart.plus({ days: 6 });
    const days = Array.from({ length: 7 }, (_, i) => getDateString(weekStart.plus({ days: i })));
    return { weekStart, weekEnd, days };
}

const PILL_ALL = "bg-slate-200/90 text-slate-800 dark:bg-slate-600/50 dark:text-slate-200";
const PILL_BRUTTOS = "bg-amber-200/90 text-amber-900 dark:bg-amber-600/50 dark:text-amber-100";
const PILL_NETTOS = "bg-emerald-200/90 text-emerald-900 dark:bg-emerald-600/50 dark:text-emerald-100";

function KpiCell({ kpi }: { kpi: KpiData | null }) {
    if (!kpi) {
        return <span className="flex h-full min-h-[2rem] items-center justify-center text-muted-foreground">—</span>;
    }
    return (
        <div className="flex h-full min-h-[2rem] w-full">
            <span
                className={`flex flex-1 items-center justify-center text-xs font-medium tabular-nums ${PILL_ALL}`}
                title="All"
            >
                {kpi.all}
            </span>
            <span
                className={`flex flex-1 items-center justify-center text-xs font-medium tabular-nums ${PILL_BRUTTOS}`}
                title="Bruttos"
            >
                {kpi.bruttos}
            </span>
            <span
                className={`flex flex-1 items-center justify-center text-xs font-medium tabular-nums ${PILL_NETTOS}`}
                title="Nettos"
            >
                {kpi.netto}
            </span>
        </div>
    );
}

function averageKpi(kpis: (KpiData | null)[]): KpiData {
    const n = kpis.length || 1;
    const sum = (get: (k: KpiData) => number) => kpis.reduce((s, k) => s + (k ? get(k) : 0), 0);
    return {
        all: Math.round(sum((k) => k.all) / n),
        bruttos: Math.round(sum((k) => k.bruttos) / n),
        netto: Math.round(sum((k) => k.netto) / n),
        averageCallDuration: sum((k) => k.averageCallDuration) / n,
        inCallTime: sum((k) => k.inCallTime) / n,
        callsPerHour: {},
    };
}

function KpisReportPage() {
    const today = getDateString(DateTime.now());
    const [anchorDate, setAnchorDate] = useState<string>(today);
    const { weekStart, weekEnd, days } = useMemo(() => getWeekRange(anchorDate), [anchorDate]);

    const { data: teamsData } = useTeams();
    const teams = teamsData?.data ?? [];

    const teamQueriesConfig = useMemo(
        () =>
            teams.map((team) => ({
                queryKey: ["teamMembersKpiByDateRange", weekStart.toISODate(), weekEnd.toISODate(), team.id],
                queryFn: () => getTeamMembersKpiByDateRange(weekStart, weekEnd, team.id),
                enabled: teams.length > 0,
            })),
        [teams, weekStart, weekEnd]
    );

    const teamQueries = useQueries({ queries: teamQueriesConfig });
    const teamKpisByDate = useMemo(() => {
        const result: Record<string, Record<string, Record<string, KpiData>>> = {};
        teams.forEach((team, idx) => {
            const response = teamQueries[idx]?.data as
                | Record<string, { members: { userId: string; kpiData: KpiData }[] }>
                | undefined;
            if (!response) return;
            result[team.id] = {};
            days.forEach((dateKey) => {
                const dayData = response[dateKey];
                if (!dayData?.members) return;
                result[team.id][dateKey] = {};
                dayData.members.forEach((m) => {
                    result[team.id][dateKey][m.userId] = m.kpiData;
                });
            });
        });
        return result;
    }, [teamQueries, teams, days]);

    const handleDateChange = (date: Date | undefined) => {
        if (date) setAnchorDate(getDateString(DateTime.fromJSDate(date)));
    };

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex items-center gap-2">
                <AppLink
                    to={APP_ROUTES_TO.TESTING}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                    <TbArrowLeft className="h-4 w-4" /> Back to experiments
                </AppLink>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">KPIs report</h1>
                    <p className="text-muted-foreground">
                        Weekly KPI table by team. All | Bruttos | Netto per day; last column is week average per
                        person/team.
                    </p>
                </div>
                <DatePicker date={anchorDate ? new Date(anchorDate) : new Date()} onDateChange={handleDateChange} />
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded-md border border-b-0 bg-muted/30 px-3 py-2 text-xs">
                <span className="text-muted-foreground">Per cell:</span>
                <span className={`rounded px-1.5 py-0.5 font-medium ${PILL_ALL}`}>All</span>
                <span className={`rounded px-1.5 py-0.5 font-medium ${PILL_BRUTTOS}`}>Bruttos</span>
                <span className={`rounded px-1.5 py-0.5 font-medium ${PILL_NETTOS}`}>Nettos</span>
            </div>
            <div className="overflow-x-auto rounded-md border">
                <table className="w-full min-w-[800px] border-collapse text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="border-r px-3 py-2 text-left font-medium">Agent / Team</th>
                            {days.map((d, i) => (
                                <th key={d} className="border-r px-2 py-2 text-center font-medium">
                                    {WEEKDAY_LABELS[i]}
                                </th>
                            ))}
                            <th className="px-3 py-2 text-center font-medium">Average</th>
                        </tr>
                    </thead>
                    <tbody>
                        {teams.map((team) => {
                            const byDate = teamKpisByDate[team.id];
                            const memberIds = team.members.map((m) => m.id);
                            const rowCells: {
                                userId: string;
                                name: string;
                                isTeamRow: boolean;
                                kpisByDay: (KpiData | null)[];
                            }[] = [];

                            memberIds.forEach((userId) => {
                                const kpisByDay = days.map((dateKey) => byDate?.[dateKey]?.[userId] ?? null);
                                rowCells.push({
                                    userId,
                                    name: team.members.find((m) => m.id === userId)?.user?.name ?? userId,
                                    isTeamRow: false,
                                    kpisByDay,
                                });
                            });

                            const teamKpisByDay = days.map((dateKey) => {
                                const dayKpis = memberIds
                                    .map((uid) => byDate?.[dateKey]?.[uid])
                                    .filter((k): k is KpiData => k != null);
                                return dayKpis.length ? averageKpi(dayKpis) : null;
                            });
                            const teamAverage = averageKpi(teamKpisByDay);

                            return (
                                <Fragment key={team.id}>
                                    {rowCells.map((row) => {
                                        const avg = averageKpi(row.kpisByDay);
                                        return (
                                            <tr key={`${team.id}-${row.userId}`} className="border-b hover:bg-muted/30">
                                                <td
                                                    className="border-r border-l-4 px-3 py-1.5 font-medium"
                                                    style={{ borderLeftColor: team.color }}
                                                >
                                                    {row.name}
                                                </td>
                                                {row.kpisByDay.map((kpi, di) => (
                                                    <td
                                                        key={days[di]}
                                                        className="w-[1%] min-w-[6rem] border-r p-0 align-middle"
                                                    >
                                                        <KpiCell kpi={kpi} />
                                                    </td>
                                                ))}
                                                <td className="w-[1%] min-w-[6rem] p-0 font-medium align-middle">
                                                    <KpiCell kpi={avg} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    <tr
                                        className="border-b-2 bg-muted/40 font-medium"
                                        style={{ borderBottomColor: team.color }}
                                    >
                                        <td
                                            className="border-r border-l-4 px-3 py-1.5"
                                            style={{ borderLeftColor: team.color }}
                                        >
                                            <span className="flex items-center gap-2">
                                                <span
                                                    className="h-2 w-2 shrink-0 rounded-full"
                                                    style={{ backgroundColor: team.color }}
                                                />
                                                {team.name}
                                            </span>
                                        </td>
                                        {teamKpisByDay.map((kpi, di) => (
                                            <td
                                                key={days[di]}
                                                className="w-[1%] min-w-[6rem] border-r p-0 align-middle"
                                            >
                                                <KpiCell kpi={kpi} />
                                            </td>
                                        ))}
                                        <td className="w-[1%] min-w-[6rem] p-0 align-middle">
                                            <KpiCell kpi={teamAverage} />
                                        </td>
                                    </tr>
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {teams.length === 0 && (
                <p className="text-center text-muted-foreground">No teams found. Create teams and add members first.</p>
            )}
        </div>
    );
}
